import { relations, sql } from 'drizzle-orm'
import { index, integer, pgTable, text, timestamp, uniqueIndex, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'
import z from 'zod/v4'

import { organization, user } from '../auth'
import { HomeConnectRequestStatusEnum } from './enums'

/**
 * One school's request to link one of its students to a parent's family account.
 *
 * A child is a `user` row; the only thing that links it to a school or a family is a `member` row.
 * "Connecting" therefore means giving the SAME child user a second `member` row in the parent's family
 * organization — never moving or copying the user, whose id every seat, PIN and progress row hangs off.
 *
 * This table is the pending-and-history layer only. Whether a child IS connected is read from the
 * `member` table, not from `status = 'ACCEPTED'`, so a child linked by any other path still reads as
 * connected and a stale row can never claim a link that was since removed.
 *
 * Better Auth's `invitation` cannot stand in for this: it has no column that can name a child, and it
 * matches the invitee strictly by email.
 */
export const HomeConnectRequest = pgTable(
  'home_connect_request',
  {
    id: uuid().primaryKey().defaultRandom(),
    childUserId: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    schoolOrganizationId: text()
      .notNull()
      .references(() => organization.id, { onDelete: 'cascade' }),
    /** Typed by the teacher; stored lowercased. Nothing in the schema holds a parent address today. */
    parentEmail: text().notNull(),
    /** Resolved at request time when the address already has an account, and again on accept. */
    parentUserId: text().references(() => user.id, { onDelete: 'set null' }),
    status: HomeConnectRequestStatusEnum().notNull().default('PENDING'),
    /** Free text from the parent, surfaced verbatim in the school's rejection notification. */
    rejectionReason: text(),
    /** Audit of where the child actually landed — the family org may be created during accept. */
    resolvedFamilyOrganizationId: text().references(() => organization.id, { onDelete: 'set null' }),
    requestedBy: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    resendCount: integer().notNull().default(0),
    requestedAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    expiresAt: timestamp({ mode: 'date', withTimezone: true }).notNull(),
    acceptedAt: timestamp({ mode: 'date', withTimezone: true }),
    rejectedAt: timestamp({ mode: 'date', withTimezone: true }),
    cancelledAt: timestamp({ mode: 'date', withTimezone: true }),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    /**
     * One live request per child, enforced by the database rather than by the router alone. A child
     * links to exactly one family, so a second school (or a second teacher) must not be able to open a
     * competing request while one is outstanding. Terminal rows are excluded, which is what lets a
     * rejected request be retried immediately.
     */
    uniqueIndex('idx_home_connect_request_one_pending_per_child')
      .on(t.childUserId)
      .where(sql`${t.status} = 'PENDING'`),
    // The roster column reads every request for one school in one pass.
    index('idx_home_connect_request_school_status').on(t.schoolOrganizationId, t.status),
    // The parent-side lookup, by the address the email was sent to.
    index('idx_home_connect_request_parent_email')
      .on(t.parentEmail)
      .where(sql`${t.status} = 'PENDING'`),
    // The expiry sweep.
    index('idx_home_connect_request_status_expires_at').on(t.status, t.expiresAt),
  ],
)

export const homeConnectRequestRelations = relations(HomeConnectRequest, ({ one }) => ({
  child: one(user, {
    fields: [HomeConnectRequest.childUserId],
    references: [user.id],
    relationName: 'homeConnectRequestChild',
  }),

  school: one(organization, {
    fields: [HomeConnectRequest.schoolOrganizationId],
    references: [organization.id],
    relationName: 'homeConnectRequestSchool',
  }),

  parent: one(user, {
    fields: [HomeConnectRequest.parentUserId],
    references: [user.id],
    relationName: 'homeConnectRequestParent',
  }),

  requester: one(user, {
    fields: [HomeConnectRequest.requestedBy],
    references: [user.id],
    relationName: 'homeConnectRequestRequester',
  }),

  resolvedFamily: one(organization, {
    fields: [HomeConnectRequest.resolvedFamilyOrganizationId],
    references: [organization.id],
    relationName: 'homeConnectRequestResolvedFamily',
  }),
}))

export const InsertHomeConnectRequestSchema = createInsertSchema(HomeConnectRequest, {
  childUserId: z.string().min(1),
  schoolOrganizationId: z.string().min(1),
  parentEmail: z.email().toLowerCase(),
  parentUserId: z.string().min(1).optional(),
  requestedBy: z.string().min(1),
  expiresAt: z.date(),
}).omit({
  id: true,
  status: true,
  rejectionReason: true,
  resolvedFamilyOrganizationId: true,
  resendCount: true,
  acceptedAt: true,
  rejectedAt: true,
  cancelledAt: true,
  createdAt: true,
  updatedAt: true,
})

export const UpdateHomeConnectRequestSchema = createUpdateSchema(HomeConnectRequest, {
  status: z.enum(HomeConnectRequestStatusEnum.enumValues),
  parentUserId: z.string().nullable(),
  rejectionReason: z.string().nullable(),
  resolvedFamilyOrganizationId: z.string().nullable(),
  resendCount: z.number().int().nonnegative(),
  expiresAt: z.date(),
  acceptedAt: z.date().nullable(),
  rejectedAt: z.date().nullable(),
  cancelledAt: z.date().nullable(),
})
  .omit({
    id: true,
    childUserId: true,
    schoolOrganizationId: true,
    parentEmail: true,
    requestedBy: true,
    requestedAt: true,
    createdAt: true,
    updatedAt: true,
  })
  .partial()

export const SelectHomeConnectRequestSchema = createSelectSchema(HomeConnectRequest, {
  id: z.uuid(),
  childUserId: z.string(),
  schoolOrganizationId: z.string(),
  parentEmail: z.string(),
  parentUserId: z.string().nullable(),
  status: z.enum(HomeConnectRequestStatusEnum.enumValues),
  rejectionReason: z.string().nullable(),
  resolvedFamilyOrganizationId: z.string().nullable(),
  requestedBy: z.string(),
  resendCount: z.number().int(),
  requestedAt: z.date(),
  expiresAt: z.date(),
  acceptedAt: z.date().nullable(),
  rejectedAt: z.date().nullable(),
  cancelledAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
})

export type InsertHomeConnectRequest = typeof HomeConnectRequest.$inferInsert
export type UpdateHomeConnectRequest = z.infer<typeof UpdateHomeConnectRequestSchema>
export type SelectHomeConnectRequest = typeof HomeConnectRequest.$inferSelect
